import { forwardRef, useState, type ReactNode } from "react";
import { TextInput, View, type ViewStyle } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { FieldInput, type FieldInputProps } from "./FieldInput";
import { MoonFieldInput } from "./MoonFieldInput";
import { MoonFormField } from "./MoonFormField";

/** Every single-line field is this tall. */
export const TEXT_FIELD_HEIGHT = 44;
/** Notes start three lines tall and grow with what she writes. */
export const NOTES_FIELD_MIN_HEIGHT = 88;

export type TextFieldProps = Omit<
  FieldInputProps,
  "multiline" | "textRole" | "tone" | "layoutStyle"
> & {
  label?: string;
  labelIcon?: LucideIcon;
  labelHint?: string;
  hint?: string;
  error?: string;
  /** The error border without a message, when the message sits elsewhere. */
  hasError?: boolean;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Several lines of her own words, starting three lines tall. */
  notes?: boolean;
  /** Inside the box before the text, for a field without a label. */
  leading?: ReactNode;
  /** Inside the box after the text, such as a unit. */
  trailing?: ReactNode;
  /** The field as a whole, for layout only (width, margins). */
  style?: ViewStyle;
};

/**
 * The shared text field: the form label, the shared box and the shared
 * editable text, with one height, one text size (16px on phones so iPhone
 * Safari does not zoom, 14px on desktop) and a visible border while focused.
 * `notes` is the multi-line version.
 *
 * Without a label it renders the box alone, for a field whose label is owned
 * by a surrounding `MoonFormField`.
 */
export const TextField = forwardRef<TextInput, TextFieldProps>(
  function TextField(
    {
      label,
      labelIcon,
      labelHint,
      hint,
      error,
      hasError = false,
      required,
      disabled,
      loading,
      notes = false,
      leading,
      trailing,
      style,
      testID,
      editable = true,
      dimWhenDisabled,
      accessibilityLabel,
      onFocus,
      onBlur,
      ...inputProps
    },
    ref,
  ) {
    const [focused, setFocused] = useState(false);
    // A labelled field already fades as a whole while disabled, so the text
    // inside does not fade a second time.
    const fieldDims = Boolean(label && disabled);
    const box = (
      <MoonFieldInput
        hasError={Boolean(error) || hasError}
        focused={focused}
        style={{
          flexDirection: "row",
          alignItems: notes ? "flex-start" : "center",
          gap: 10,
          ...(label ? null : style),
        }}
      >
        {leading ? (
          <View style={{ paddingTop: notes ? 12 : 0 }}>{leading}</View>
        ) : null}
        <FieldInput
          {...inputProps}
          ref={ref}
          testID={testID}
          // Screen readers hear the visible label, not only the placeholder.
          accessibilityLabel={accessibilityLabel ?? label}
          textRole="adaptive"
          multiline={notes}
          editable={editable && !disabled}
          dimWhenDisabled={fieldDims ? false : dimWhenDisabled}
          onFocus={(event) => {
            setFocused(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setFocused(false);
            onBlur?.(event);
          }}
          layoutStyle={{
            flex: 1,
            // Lets the text give way to a trailing button or unit instead of
            // pushing it out of the box.
            minWidth: 0,
            minHeight: notes ? NOTES_FIELD_MIN_HEIGHT : TEXT_FIELD_HEIGHT,
            paddingVertical: 10,
          }}
        />
        {trailing ? (
          <View style={{ paddingTop: notes ? 12 : 0 }}>{trailing}</View>
        ) : null}
      </MoonFieldInput>
    );

    if (!label) return box;

    return (
      <MoonFormField
        label={label}
        labelIcon={labelIcon}
        labelHint={labelHint}
        hint={hint}
        error={error}
        required={required}
        disabled={disabled}
        loading={loading}
        loadingHeight={notes ? NOTES_FIELD_MIN_HEIGHT : TEXT_FIELD_HEIGHT}
        style={style}
      >
        {box}
      </MoonFormField>
    );
  },
);
